/**
 * SMS appointment reminder text template (the exact body sent by the reminder cron).
 *
 * SHARED FILE: the canonical copy lives in packages/utils/src/sms-reminder-template.ts (used by
 * apps/web via @book4/utils for the live phone preview in SMS settings).
 * apps/api/src/lib/sms-reminder-template.ts is a byte-for-byte copy so production `node dist/`
 * never loads a workspace package's .ts source. A parity test in the API fails if the two
 * drift, so edit both together. No imports allowed here.
 */

/** Format startAt the same way live SMS reminder bodies do (en-GB, Europe/London). */
export function formatReminderDateTime(startAt: Date): string {
  return startAt.toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/London",
  });
}

/**
 * Public booking page URL for a shop (used as the reply route in texts).
 * Returns null when there is no slug.
 */
export function buildShopBookingUrl(frontendUrl: string, slug: string | null | undefined): string | null {
  const s = slug?.trim();
  if (!s) return null;
  return `${frontendUrl.replace(/\/$/, "")}/book/${encodeURIComponent(s)}`;
}

/**
 * GSM 03.38 default alphabet (7-bit). A text using only these characters is sent as GSM-7:
 * 160 characters in one part, 153 per part when longer. A single character outside it switches
 * the whole text to UCS-2 (70 characters in one part, 67 per part).
 */
const GSM7_BASIC =
  "@\u00a3$\u00a5\u00e8\u00e9\u00f9\u00ec\u00f2\u00c7\n\u00d8\u00f8\r\u00c5\u00e5\u0394_\u03a6\u0393\u039b\u03a9\u03a0\u03a8\u03a3\u0398\u039e" +
  "\u00c6\u00e6\u00df\u00c9 !\"#\u00a4%&'()*+,-./0123456789:;<=>?\u00a1ABCDEFGHIJKLMNOPQRSTUVWXYZ\u00c4\u00d6\u00d1\u00dc\u00a7" +
  "\u00bfabcdefghijklmnopqrstuvwxyz\u00e4\u00f6\u00f1\u00fc\u00e0";
/** GSM-7 extension table: each costs 2 characters (escape + char). */
const GSM7_EXTENDED = "^{}\\[~]|\u20ac\f";
const GSM7_BASIC_SET = new Set(Array.from(GSM7_BASIC));
const GSM7_EXTENDED_SET = new Set(Array.from(GSM7_EXTENDED));

/** Characters with a readable GSM-7 replacement (checked before accent folding). */
const GSM7_REPLACEMENTS: Record<string, string> = {
  "\u2018": "'", "\u2019": "'", "\u201a": "'", "\u201b": "'", "\u2032": "'", "\u02bc": "'", "`": "'", "\u00b4": "'",
  "\u201c": '"', "\u201d": '"', "\u201e": '"', "\u201f": '"', "\u2033": '"', "\u00ab": '"', "\u00bb": '"',
  "\u2010": "-", "\u2011": "-", "\u2012": "-", "\u2013": "-", "\u2014": "-", "\u2015": "-", "\u2212": "-",
  "\u2026": "...", "\u2022": "-", "\u00b7": "-", "\u00d7": "x", "\u2122": "TM", "\u00a9": "(c)", "\u00ae": "(R)",
  "\u00df": "ss", "\u00e6": "ae", "\u00c6": "AE", "\u00f8": "o", "\u00d8": "O", "\u0153": "oe", "\u0152": "OE",
  "\u0142": "l", "\u0141": "L", "\u0111": "d", "\u0110": "D", "\u00f0": "d", "\u00d0": "D", "\u00fe": "th", "\u00de": "Th",
  "\u0131": "i", "\t": " ",
};

/**
 * Make text safe for GSM-7 so a reminder never switches to UCS-2 (70-character parts):
 * curly quotes become straight, dashes become hyphens, the ellipsis becomes "...", accented
 * Latin letters are transliterated to plain letters (Café -> Cafe), unusual spaces become
 * normal spaces, and anything else outside the GSM-7 alphabet (emoji, symbols, other scripts)
 * is removed. Runs of spaces are collapsed and the result is trimmed.
 */
export function normalizeSmsTextToGsm7(text: string): string {
  const replaced = Array.from(text ?? "")
    .map((ch) => GSM7_REPLACEMENTS[ch] ?? ch)
    .join("")
    // Split accented letters into base letter + accent (and fold compatibility forms such as
    // non-breaking spaces and full-width letters), then drop the accents.
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u00a0\u1680\u2000-\u200a\u202f\u205f\u3000]/g, " ");
  let out = "";
  for (const ch of Array.from(replaced)) {
    const mapped = GSM7_REPLACEMENTS[ch] ?? ch;
    for (const c of Array.from(mapped)) {
      if (GSM7_BASIC_SET.has(c) || GSM7_EXTENDED_SET.has(c)) out += c;
    }
  }
  return out
    .replace(/ {2,}/g, " ")
    .replace(/ +([.,!?)])/g, "$1")
    .replace(/\( +/g, "(")
    .trim();
}

export type SmsSegmentInfo = {
  encoding: "GSM-7" | "UCS-2";
  /** Characters as the network counts them (GSM-7 extension characters count 2). */
  length: number;
  /** Number of parts the network splits the text into (0 for an empty text). */
  segments: number;
  /** Characters that fit in a single-part text for this encoding (160 or 70). */
  singleSegmentLimit: number;
};

/**
 * How a text is split into parts. Informational only: Book4 charges per send, not per part.
 * GSM-7: up to 160 in one part, else 153 per part. UCS-2: up to 70 in one part, else 67 per part.
 * Parts never split a GSM-7 escape pair or a UTF-16 surrogate pair.
 */
export function smsSegmentCount(text: string): SmsSegmentInfo {
  const chars = Array.from(text ?? "");
  const gsm = chars.every((c) => GSM7_BASIC_SET.has(c) || GSM7_EXTENDED_SET.has(c));
  const costs = chars.map((c) => (gsm ? (GSM7_EXTENDED_SET.has(c) ? 2 : 1) : c.length));
  const length = costs.reduce((a, b) => a + b, 0);
  const single = gsm ? 160 : 70;
  const multi = gsm ? 153 : 67;
  let segments = 0;
  if (length > 0 && length <= single) {
    segments = 1;
  } else if (length > single) {
    let used = 0;
    segments = 1;
    for (const cost of costs) {
      if (used + cost > multi) {
        segments++;
        used = 0;
      }
      used += cost;
    }
  }
  return { encoding: gsm ? "GSM-7" : "UCS-2", length, segments, singleSegmentLimit: single };
}

/** Hard ceiling for every SMS reminder: one GSM-7 text (extension characters count 2). */
export const SMS_REMINDER_MAX_SEPTETS = 160;

/** Customer name used by the settings preview, so it shows the same shape guests receive. */
export const SAMPLE_REMINDER_CUSTOMER_NAME = "Sam Taylor";

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Short reminder date and time in Europe/London, e.g. "Tue 7 Oct 2:30pm" ("Tue 7 Oct 2pm" on
 * the hour). Month names are fixed here so ICU updates ("Sep" vs "Sept") never change the length.
 */
export function formatReminderDateTimeShort(startAt: Date): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    weekday: "short",
    day: "numeric",
    month: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(startAt);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const hour24 = Number(get("hour")) % 24;
  const minute = get("minute").padStart(2, "0");
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const suffix = hour24 < 12 ? "am" : "pm";
  const time = minute === "00" ? `${hour12}${suffix}` : `${hour12}:${minute}${suffix}`;
  return `${get("weekday")} ${Number(get("day"))} ${MONTHS_SHORT[Number(get("month")) - 1] ?? ""} ${time}`;
}

/** Length of a GSM-7 text as the network counts it (extension characters count 2). */
function septets(text: string): number {
  let n = 0;
  for (const ch of Array.from(text)) n += GSM7_EXTENDED_SET.has(ch) ? 2 : 1;
  return n;
}

/** Longest prefix that fits in `max` septets, never splitting an extension character. */
function cutToSeptets(text: string, max: number): string {
  let used = 0;
  let out = "";
  for (const ch of Array.from(text)) {
    const cost = GSM7_EXTENDED_SET.has(ch) ? 2 : 1;
    if (used + cost > max) break;
    used += cost;
    out += ch;
  }
  return out;
}

/**
 * Shorten a GSM-7 name to at most `max` septets at a word boundary, with no ellipsis. Cuts
 * mid-word only when the first word alone is too long. Trailing joiners ("&", "-", ",") go.
 */
export function truncateGsm7AtWord(text: string, max: number): string {
  if (septets(text) <= max) return text;
  if (max <= 0) return "";
  const hard = cutToSeptets(text, max);
  const next = Array.from(text)[Array.from(hard).length];
  let cut = hard;
  if (next !== " ") {
    const lastSpace = hard.lastIndexOf(" ");
    if (lastSpace > 0) cut = hard.slice(0, lastSpace);
  }
  const tidy = (s: string) => s.replace(/[\s\-,&/:;+.([{|\\]+$/, "").trim();
  return tidy(cut) || tidy(hard) || hard.trim();
}

export type ReminderContact =
  | { kind: "phone"; value: string }
  | { kind: "link"; value: string }
  | { kind: "shop" }
  | { kind: "none" };

type ReminderParts = {
  name: string;
  shop: string;
  service: string;
  dateTime: string;
  contact: ReminderContact;
  /** Filler words dropped ("your appointment at", "is on", "Need to change it?"). */
  compact: boolean;
};

function renderReminder(p: ReminderParts): string {
  const service = p.service ? ` (${p.service})` : "";
  const c = p.contact;
  let contact = "";
  if (p.compact) {
    if (c.kind === "phone") contact = ` To change call ${c.value}.`;
    else if (c.kind === "link") contact = ` To change: ${c.value}`;
    else if (c.kind === "shop") contact = " To change, contact the shop.";
    const prefix = p.name ? `Hi ${p.name}, reminder: ` : "Reminder: ";
    // Shop name dropped (too long to cut cleanly): the sender name still shows the shop.
    const what = p.shop ? `${p.shop}${service}` : `your appointment${service}`;
    return normalizeSmsTextToGsm7(`${prefix}${what} on ${p.dateTime}.${contact}`);
  }
  if (c.kind === "phone") contact = ` Need to change it? Call ${c.value}.`;
  else if (c.kind === "link") contact = ` Need to change it? Visit ${c.value}`;
  else if (c.kind === "shop") contact = " Need to change it? Contact the shop.";
  const prefix = p.name ? `Hi ${p.name}, reminder: your` : "Reminder: Your";
  const at = p.shop ? ` at ${p.shop}` : "";
  return normalizeSmsTextToGsm7(`${prefix} appointment${at}${service} is on ${p.dateTime}.${contact}`);
}

/** Shortest the service name is cut to before the shop name is touched. */
const SERVICE_MIN_SEPTETS = 10;
/** Shortest the shop name is cut to while the service name is still shown. */
const SHOP_MIN_SEPTETS = 12;
/**
 * Absolute floor for a shortened shop name. A cut that would leave fewer characters than this,
 * or only small words such as "The", drops the shop name instead (the sender name still shows it).
 */
export const SHOP_FLOOR_SEPTETS = 6;
/** Longest shop phone shown in a reminder before it is tidied or replaced by "contact the shop". */
export const REMINDER_PHONE_MAX_SEPTETS = 20;

/** Words that can't stand on their own as a shortened shop name, or end one. */
const SHOP_NAME_SMALL_WORDS = new Set([
  "the", "a", "an", "and", "&", "of", "at", "by", "for", "in", "on", "to", "with", "+",
  "le", "la", "les", "el", "los", "las", "il", "der", "die", "das",
]);

const isSmallWord = (w: string) => SHOP_NAME_SMALL_WORDS.has(w.toLowerCase());

/**
 * Shorten a shop name to at most `max` septets at a word boundary. Trailing small words go
 * ("The Barber of" becomes "The Barber"). Returns "" when what is left would be under
 * SHOP_FLOOR_SEPTETS characters or only small words (a bare "The"): drop the name instead.
 */
export function shortenShopName(name: string, max: number): string {
  if (septets(name) <= max) return name;
  const words = truncateGsm7AtWord(name, max).split(" ").filter(Boolean);
  while (words.length > 1 && isSmallWord(words[words.length - 1]!)) words.pop();
  const cut = words.join(" ").replace(/[\s\-,&/:;+.([{|\\]+$/, "").trim();
  if (septets(cut) < SHOP_FLOOR_SEPTETS) return "";
  if (cut.split(" ").every(isSmallWord)) return "";
  return cut;
}

/** Shorten the service name by exactly the overflow (word boundary), never below `min` septets. */
function shrinkService(p: ReminderParts, min: number): ReminderParts {
  const overflow = septets(renderReminder(p)) - SMS_REMINDER_MAX_SEPTETS;
  if (overflow <= 0 || !p.service) return p;
  const target = Math.max(min, septets(p.service) - overflow);
  return { ...p, service: truncateGsm7AtWord(p.service, target) };
}

/**
 * Shorten the shop name by exactly the overflow, never below `min` septets. When no clean cut
 * exists (it would be a bare "The" or under 6 characters): leave the name for a later step, or
 * drop it when `dropIfNoCleanCut` is set.
 */
function shrinkShop(p: ReminderParts, min: number, dropIfNoCleanCut: boolean): ReminderParts {
  const overflow = septets(renderReminder(p)) - SMS_REMINDER_MAX_SEPTETS;
  if (overflow <= 0 || !p.shop) return p;
  const target = Math.max(min, septets(p.shop) - overflow);
  const cut = shortenShopName(p.shop, target);
  if (cut) return { ...p, shop: cut };
  return dropIfNoCleanCut ? { ...p, shop: "" } : p;
}

/**
 * Keep a long shop phone to about 20 characters: the leading number without trailing notes
 * ("07700 900123 ext 4, ask for Jo" becomes "07700 900123"), then without spaces, and when it
 * still does not fit (or has no real number) the text says "contact the shop" instead.
 */
export function capReminderPhone(contact: ReminderContact): ReminderContact {
  if (contact.kind !== "phone" || septets(contact.value) <= REMINDER_PHONE_MAX_SEPTETS) return contact;
  const lead = (contact.value.match(/^\+?[\d\s()\-.]+/)?.[0] ?? "").replace(/[\s(\-.]+$/, "").trim();
  const digits = lead.replace(/\D/g, "").length;
  if (digits >= 6 && septets(lead) <= REMINDER_PHONE_MAX_SEPTETS) return { kind: "phone", value: lead };
  // "+44 (0) 20 ..." drops the "(0)" trunk prefix when written without spaces.
  const compact = lead.replace(/\(0\)/g, "").replace(/[\s()\-.]/g, "");
  if (digits >= 6 && septets(compact) <= REMINDER_PHONE_MAX_SEPTETS) return { kind: "phone", value: compact };
  return { kind: "shop" };
}

/** Titles skipped before the first name in a greeting, compared without a trailing full stop. */
const GREETING_TITLES = new Set(["dr", "mr", "mrs", "ms", "miss", "mx", "prof"]);

/**
 * First name for a client greeting ("Hi Jane,"), or "" for the generic greeting. Titles (Dr, Mr,
 * Mrs, Ms, Miss, Mx, Prof, with or without a full stop) are skipped, so "Dr Jane Smith" gives
 * "Jane". A title followed by a single word ("Mrs Smith") is a surname, so it gives "". The name
 * must have at least 2 letters ("J" or "J." gives ""), so an initial never reads as a name.
 */
export function greetingFirstName(name: string | null | undefined): string {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  let i = 0;
  while (i < words.length && GREETING_TITLES.has(words[i].toLowerCase().replace(/\.$/, ""))) i++;
  if (i > 0 && words.length - i < 2) return "";
  const first = (words[i] ?? "").replace(/[.,;:!?]+$/, "");
  const letters = first.match(/[a-z\u00c0-\u024f]/gi)?.length ?? 0;
  return letters >= 2 ? first : "";
}

/**
 * A phone value that can go in a message, or "" when there is none. A value with no digits at all
 * ("n/a", "ask in shop") is treated as no phone, so it is never sent to clients verbatim.
 */
export function usableMessagePhone(phone: string | null | undefined): string {
  const trimmed = (phone ?? "").trim();
  return /[0-9]/.test(trimmed) ? trimmed : "";
}

/**
 * The single SMS appointment reminder builder, used by the reminder cron (send path) and the
 * settings phone preview, so the two can never drift. Output is normalised to GSM-7 and is
 * ALWAYS one text: 160 septets or fewer, extension characters ({}[]~^|\ and the euro sign)
 * counting 2. Credits stay at 1 per send.
 *
 * Full text when it fits. The customer is greeted by first name only (greetingFirstName: titles
 * skipped, at least 2 letters, otherwise no name). A shop phone with no digits counts as no phone
 * (the booking link is used, or "contact the shop" when there is no link either):
 *   "Hi Sam, reminder: your appointment at Shop (Service) is on 25 Sept 2026, 14:00.
 *    Need to change it? Call 07700 900123."
 * Texts go out from an alphanumeric sender name, which guests cannot reply to, so the body ends
 * with a way to get in touch: the shop phone when set (always preferred, it is shorter than the
 * booking link), otherwise the booking page link.
 *
 * Shrink order. Each step is applied only while the text is still over 160, and steps stack:
 *   1. Short date and time: "Tue 7 Oct 2:30pm".
 *   2. A shop phone longer than about 20 characters is tidied to the bare number, or replaced by
 *      "contact the shop" (before any name is shortened).
 *   3. Customer first name dropped.
 *   4. Filler words dropped: "Reminder: Shop (Service) on Tue 7 Oct 2pm. To change call 0770...".
 *      Filler goes before any real name is cut because it carries no information.
 *   5. Booking link without "https://" and "www." (phones still make it tappable).
 *   6. Service name cut at a word boundary, down to 10 characters.
 *   7. Shop name cut at a word boundary, down to 12 characters (skipped when the only cut is a
 *      bare "The" or under 6 characters).
 *   8. Service name dropped.
 *   9. Shop name cut down to 6 characters; when no clean cut exists the shop name is dropped
 *      ("Reminder: your appointment on ..."). The sender name still shows the shop.
 *  10. Reply route replaced by "To change, contact the shop." (only for an absurdly long link).
 *  11. Shop name dropped, if it still does not fit.
 *  The essentials always stay: when (day and time) and how to change it.
 *  A final hard cut (fitSmsToSingleSegment) backs this up, but the steps above always fit first.
 */
export type SmsReminderParams = {
  businessName: string;
  serviceName: string;
  startAt: Date;
  customerName?: string | null;
  shopPhone?: string | null;
  bookingUrl?: string | null;
};

export function buildSmsReminderBody(params: SmsReminderParams): string {
  return buildReminder(params).body;
}

/** Shrink-to-fit core shared by buildSmsReminderBody and previewReminderShopName. */
function buildReminder(params: SmsReminderParams): { body: string; parts: ReminderParts; fullShop: string } {
  const firstName = greetingFirstName(normalizeSmsTextToGsm7(params.customerName ?? ""));
  const phone = normalizeSmsTextToGsm7(usableMessagePhone(params.shopPhone));
  const url = normalizeSmsTextToGsm7(params.bookingUrl ?? "");
  // A phone with no digits is never shown: the booking link is used instead, or, with no link,
  // "contact the shop" (the shop did enter something, just not a number).
  const contact: ReminderContact = phone
    ? { kind: "phone", value: phone }
    : url
      ? { kind: "link", value: url }
      : (params.shopPhone ?? "").trim()
        ? { kind: "shop" }
        : { kind: "none" };

  const fullShop = normalizeSmsTextToGsm7(params.businessName) || "your shop";
  let p: ReminderParts = {
    name: firstName,
    shop: fullShop,
    service: normalizeSmsTextToGsm7(params.serviceName),
    dateTime: formatReminderDateTime(params.startAt),
    contact,
    compact: false,
  };

  const steps: Array<(x: ReminderParts) => ReminderParts> = [
    (x) => ({ ...x, dateTime: formatReminderDateTimeShort(params.startAt) }),
    (x) => ({ ...x, contact: capReminderPhone(x.contact) }),
    (x) => ({ ...x, name: "" }),
    (x) => ({ ...x, compact: true }),
    (x) =>
      x.contact.kind === "link"
        ? { ...x, contact: { kind: "link", value: x.contact.value.replace(/^https?:\/\//i, "").replace(/^www\./i, "") } }
        : x,
    (x) => shrinkService(x, SERVICE_MIN_SEPTETS),
    (x) => shrinkShop(x, SHOP_MIN_SEPTETS, false),
    (x) => ({ ...x, service: "" }),
    (x) => shrinkShop(x, SHOP_FLOOR_SEPTETS, true),
    (x) => (x.contact.kind === "none" ? x : { ...x, contact: { kind: "shop" } }),
    (x) => ({ ...x, shop: "" }),
  ];
  for (const step of steps) {
    if (septets(renderReminder(p)) <= SMS_REMINDER_MAX_SEPTETS) break;
    p = step(p);
  }
  return { body: fitSmsToSingleSegment(renderReminder(p)), parts: p, fullShop };
}

/** Typical worst case used by the setup-time check: a long service name and a first name. */
export const REMINDER_CHECK_SERVICE_NAME = "Skin Fade with Beard Trim and Hot Towel Finish";
export const REMINDER_CHECK_CUSTOMER_NAME = "Alexandra";
/** Stand-in shop phone for the check when the shop has not added one yet. */
export const REMINDER_CHECK_SHOP_PHONE = "07700 900123";
/** A fixed long date (two-digit day, minutes) so the check does not depend on today's date. */
const REMINDER_CHECK_START_AT = new Date("2026-09-30T11:45:00.000Z");

/**
 * Setup-time check for the shop name: runs the same builder the reminder cron uses with a typical
 * worst case (long service name, a first name, the shop phone) and reports whether the shop name
 * would be shortened in reminders, and how it would show. `body` is the example text.
 */
export function previewReminderShopName(params: {
  businessName: string;
  shopPhone?: string | null;
}): { fullName: string; shownAs: string; shortened: boolean; dropped: boolean; body: string } {
  const r = buildReminder({
    businessName: params.businessName,
    serviceName: REMINDER_CHECK_SERVICE_NAME,
    startAt: REMINDER_CHECK_START_AT,
    customerName: REMINDER_CHECK_CUSTOMER_NAME,
    shopPhone: usableMessagePhone(params.shopPhone) ? params.shopPhone : REMINDER_CHECK_SHOP_PHONE,
  });
  return {
    fullName: r.fullShop,
    shownAs: r.parts.shop,
    shortened: r.parts.shop !== r.fullShop,
    /** The shop name is left out of the text entirely (no clean cut exists). */
    dropped: r.parts.shop === "",
    body: r.body,
  };
}

/**
 * Normalise to GSM-7 and hard-cut to one text (160 septets) if needed, never splitting an
 * extension character. `truncated` is true only when characters were actually cut off after
 * normalising (a UCS-2 body that fits once normalised is not truncated); `normalized` reports
 * that the text was changed by GSM-7 normalisation.
 */
export function enforceSingleSmsSegment(text: string): {
  text: string;
  truncated: boolean;
  normalized: boolean;
  originalLength: number;
  originalSegments: number;
} {
  const original = smsSegmentCount(text ?? "");
  const normalized = normalizeSmsTextToGsm7(text ?? "");
  const over = septets(normalized) > SMS_REMINDER_MAX_SEPTETS;
  const out = over ? cutToSeptets(normalized, SMS_REMINDER_MAX_SEPTETS).trimEnd() : normalized;
  return {
    text: out,
    truncated: over,
    normalized: normalized !== (text ?? ""),
    originalLength: original.length,
    originalSegments: original.segments,
  };
}

/** Text guaranteed to be one GSM-7 part (see enforceSingleSmsSegment). */
export function fitSmsToSingleSegment(text: string): string {
  return enforceSingleSmsSegment(text).text;
}
